import { action, computed, observable, observe } from 'mobx';
import { Store } from './mainStore';
import UtilsStore from '@/store/utilsStore';
import { packs } from '@/store/ProductStore';
import { Scan as ScanAPI, ScanProperties, SurfaceModel } from '@/store/engine/openapi/generated';
import { NonNullableArray } from '@/store/engine/modules/EngineModule';
import { ScanModes } from '@/store/agentStore';
import { FootScanImportData } from '@leopoly/arize-eu-insole/dist/visualizer/common';

export default class BiometricStore {
	@observable private userScansList: ScanAPI[] = [];
	@observable private scans: Scans = {
		left: {},
		right: {}
	};
	store: Store;
	@observable areScansUpToDate = false;
	@observable fetchingScans = false;
	@observable differentScanMetadataDetected = false;
	// we distinguish thhis from patient.fitstationProfile, as order also can carry lastvalidsession
	@observable public lastValidScanningSessionID: string = '';

	@action
	clear(type: 'leftscan' | 'rightscan' | 'both' = 'both') {
		switch (type) {
			case 'leftscan':
				URL.revokeObjectURL(this.scans.left.blobURL || '');
				this.scans.left = {};

				break;
			case 'rightscan':
				URL.revokeObjectURL(this.scans.right.blobURL || '');
				this.scans.right = {};
				break;
			case 'both':
				URL.revokeObjectURL(this.scans.left.blobURL || '');
				URL.revokeObjectURL(this.scans.right.blobURL || '');
				this.scans.left = {};
				this.scans.right = {};
				break;
		}
		this.userScansList = [];
	}

	private async fetchAllScansData() {
		this.fetchingScans = true;
		while (!this.lastValidScanningSessionID) {
			await UtilsStore.wait(100);
		}
		// this is cause, when we are going directly to /summmary, there is no sdk initialized, need to rework
		await this.fetchScans();
		await this.fetch3DScanSurfaceModel(this.scans.left.scan?.measurementID, this.scans.right.scan?.measurementID);
		await this.fetchScansProperties(this.scans.left.scan?.measurementID, this.scans.right.scan?.measurementID);
		this.onScansFetched();
		this.fetchingScans = false;
		this.areScansUpToDate = true;
	}

	@action.bound
	private subscribe() {
		// observe(this.store._patient, "userRecordID", (change) => {
		// 	if (change.newValue && !this.store._patient.creatingPatient) {
		// 		this.fetchAllScansData();
		// 	}
		// });

		window.addEventListener('beforeunload', (e) => {
			URL.revokeObjectURL(this.scans.left.blobURL || '');
			URL.revokeObjectURL(this.scans.right.blobURL || '');
		});
	}

	private onScansFetched() {
		this.store.product.assignProductPackFromScans(this.leftScanExists, this.rightScanExists);
		this.store.prescription.validatePrescriptionByScans(this.leftScanExists, this.rightScanExists);
		// !this.store._patient.creatingPatient && PATIENT_REWORK
		if (!this.store.agent.scannedInThisSession) {
			this.store.agent.oneFootScan = this.onlyOneScanExists;
		}
		if (this.scans.right.scan && this.scans.left.scan) {
			this.store.biometric.differentScanMetadataDetected = !this.checkIfScansHasSameMeta(this.scans.right.scan, this.scans.left.scan);
		}
	}

	private async fetchScans() {
		let scans = await this.store.engine.biometric.getScans(this.lastValidScanningSessionID);
		if (!scans) throw new Error('Scans not found');
		this.userScansList = scans;
		let scan = this.userScansList[this.userScansList.length - 1];
		if (scan) {
			this.setScanMode(scan);
		}

		let leftScan = this.findLastScan(scans, 'left');
		if (leftScan) {
			this.scans.left = {
				scan: leftScan
			};
		}
		let rightScan = this.findLastScan(scans, 'right');
		if (rightScan) {
			this.scans.right = {
				scan: rightScan
			};
		}
	}

	private async fetch3DScanSurfaceModel(leftMeasurementID?: string, rightMeasurementID?: string) {
		this.scans.left.surfaceModel = await this.store.engine.biometric.get3DScanSurfaceModel(leftMeasurementID);
		this.scans.right.surfaceModel = await this.store.engine.biometric.get3DScanSurfaceModel(rightMeasurementID);
		this.scans.left.blobURL = this.createBlobURL(this.scans.left.surfaceModel);
		this.scans.right.blobURL = this.createBlobURL(this.scans.right.surfaceModel);
	}

	private async fetchScansProperties(leftMeasurementID?: string, rightMeasurementID?: string) {
		this.scans.left.properties = await this.store.engine.biometric.get3DScanProperties(leftMeasurementID);
		this.scans.right.properties = await this.store.engine.biometric.get3DScanProperties(rightMeasurementID);
	}

	@action.bound
	async waitForLatestScans() {
		await new Promise((p) => setTimeout(p, 100));
		return !this.areScansUpToDate ? this.waitForLatestScans() : this.scans;
	}

	@action.bound
	async getLatestScans() {
		if (!this.areScansUpToDate && !this.fetchingScans) {
			await this.fetchAllScansData();
		}

		return this.waitForLatestScans();
	}

	@action.bound
	async getUserScansList() {
		if (!this.areScansUpToDate && !this.fetchingScans) {
			await this.fetchAllScansData();
		}

		await new Promise((p) => setTimeout(p, 100));
		return !this.areScansUpToDate ? this.getLatestScans() : this.userScansList;
	}

	findLastScan(scans: Array<ScanAPI>, type: 'left' | 'right') {
		if (!scans) {
			return undefined;
		}
		const platformType = type === 'left' ? 'SCAN_SUBTYPE_FLAT_LEFT_FOOT' : 'SCAN_SUBTYPE_FLAT_RIGHT_FOOT';
		const reversedScans = [...scans].reverse();
		return reversedScans?.find((i) => i && i.subtype && i.subtype === platformType && i.isComplete);
	}

	createBlobURL(surfaceModel?: SurfaceModel) {
		if (!surfaceModel?.data) {
			return '';
		}
		const scanDataBuffer = UtilsStore.base64ToArrayBuffer(surfaceModel.data);
		const blob = new Blob([scanDataBuffer], { type: 'application/object' });
		return URL.createObjectURL(blob);
	}

	private convertScanModeToVisualizer(scanMode: ScanModes) {
		//  ['FOAMBOX', 'NON_WEIGHTED', 'WEIGHTED', 'SEMI_WEIGHTED'] as const;
		if (scanMode === 'semi-weighted') {
			return 0; //  SemiWeighted = 0,
		} else if (scanMode === 'weighted') {
			return 1; // 	Weighted = 1,
		} else if (scanMode === 'non-weighted') {
			return 2; //   NonWeighted = 2,
		} else if (scanMode === 'foambox') {
			return 3; //   FoamBox = 3,
		} else {
			return 4; //   NumFootscanTypes = 4
		}
	}

	public buildVisualizerFootScan(type: 'leftscan' | 'rightscan'): FootScanImportData | null {
		const scan = type === 'leftscan' ? this.scans.left : this.scans.right;
		if (!scan.surfaceModel) {
			return null;
		}
		const buf = UtilsStore.base64ToArrayBuffer(scan.surfaceModel.data);
		let format: 'drc' | 'obj' = 'drc';
		if (scan.surfaceModel.encoding == 'obj' || scan.surfaceModel.encoding == 'OBJ') {
			format = 'obj';
		}
		const bytes = new Uint8Array(buf);
		return {
			fileBytes: bytes,
			format,
			importUnits: 'mm',
			scanMode: this.convertScanModeToVisualizer(this.store.agent.scanMode),
			importAxes: {
				rightAxis: 0, // AxisNative.X,
				upAxis: 5, // AxisNative.NegZ,
				forwardAxis: 1 // AxisNative.Y
			}
		};
	}

	setScanMode(scan: ScanAPI) {
		const scanMode = scan?.meta?.scanMode;
		if (scanMode) {
			this.store.agent.setScanSettings(scanMode);
		}
	}

	@computed get leftScanExists() {
		return !!this.scans.left?.scan;
	}

	@computed get rightScanExists() {
		return !!this.scans.right?.scan;
	}

	@computed get onlyOneScanExists() {
		return !!this.scans.left?.scan !== !!this.scans.right?.scan;
	}

	@computed get oneScanExists() {
		return !!this.scans.left?.scan || !!this.scans.right?.scan;
	}

	@computed get bothScansExist() {
		return !!this.scans.left?.scan && !!this.scans.right?.scan;
	}

	@computed get appropriateNumberOfScansExist() {
		return (this.store.agent.oneFootScan && this.oneScanExists) || (!this.store.agent.oneFootScan && this.bothScansExist);
	}

	constructor(mainStore: Store) {
		this.store = mainStore;
		this.subscribe();
	}

	private checkIfScansHasSameMeta(scanMeta: ScanAPI, scanMeta2: ScanAPI) {
		let sameScanMode = scanMeta?.meta?.scanMode === scanMeta2?.meta?.scanMode;
		let sameProduct = scanMeta?.meta?.product === scanMeta2?.meta?.product;
		let sameScannerType = scanMeta?.meta?.scannerType === scanMeta2?.meta?.scannerType;

		return sameScanMode && sameProduct && sameScannerType;
	}
}

type Scan = {
	scan?: ScanAPI;
	surfaceModel?: SurfaceModel;
	properties?: ScanProperties;
	blobURL?: string;
};

type Scans = {
	left: Scan;
	right: Scan;
};
